/* eslint-disable indent */
import { permission } from '@/api/services/user';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { SelectLayout } from '@/components/SelectLayout';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import SquareGrid from '@/components/Util/reactSquareGrid';
import colors from '@/config/colors';
import { useStoreActions, useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { AntDesign } from '@expo/vector-icons';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { useNavigation } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import encryptedStorage from '../../stores/encryptedStorage';
import ExerciseCard from './ExerciseCard';
import { TASKS } from './constants';
import { useDialog } from '@/hooks/useDialog';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { ExerciseSettings, Patient, Set } from '@/stores/store.types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { isPatientApp } from '@/config/constants';

interface SelectExerciseProps {
  exerciseClass: string;
  patientId: string;
  setId: string;
  isNetworkError?: string;
  exerciseSettings: Record<string, string | ExerciseSettings>;
  isSelf: boolean;
  selectedPatient?: Patient;
  activeSets?: unknown;
}

const styles = StyleSheet.create({
  darkGrey: {
    color: colors.darkGrey,
  },
  inActiveStyle: {
    opacity: 0.5,
  },
  centerAlignment: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  marginLeft: { marginLeft: 4 },
});

export function SelectExercise(props: SelectExerciseProps) {
  const { navigate, ...navigation } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  const { showDialog } = useDialog();

  const { isTherapistDemo, toolTipNumber, selfTrainingSelection } =
    useStoreState((state) => state);

  const { setToolTipNumber } = useStoreActions((actions) => ({
    setToolTipNumber: actions.setToolTipNumber,
  }));

  const [exerciseSettings, setExerciseSettings] = useState(
    props.exerciseSettings.exerciseSettings,
  );
  const [activeExerciseSet, setActiveExerciseSet] = useState<{
    [key: string]: boolean;
  }>({ Sprechen: false, Lesen: false, Schreiben: false, Verstehen: false });

  const {
    patientId,
    setId,
    isSelf,
    selectedPatient,
    activeSets,
    exerciseClass: setType, // setType = wordset | sentenceset | textset
  } = props;

  const isTextset = setType === 'textset';
  const tasksKey = isTextset ? setType : 'default';

  const toggleActiveLocalSetSettings = async (
    id: string,
    key: 'Lesen' | 'Schreiben' | 'Sprechen' | 'Verstehen',
  ) => {
    try {
      const { exerciseSettings } = (await encryptedStorage().getItem(
        setId,
      )) as Set;
      const prevState = exerciseSettings[key]?.active;
      const toChange = {
        ...exerciseSettings,
        [key]: {
          ...exerciseSettings[key],
          active: !prevState,
        },
      };
      setExerciseSettings(toChange);
      const newData = {
        ...props.exerciseSettings,
        exerciseSettings: toChange,
      };

      await encryptedStorage().setItem(id, newData);
    } catch (error) {
      logError('Could not activate local set', error);
    }
  };

  const responsiveFontHeight = useResponsiveHeight(2);

  const getResponsiveFontSize = (factor: number) => {
    return responsiveFontHeight * factor;
  };

  const responsizeIconSizeBig = getResponsiveFontSize(8);

  let responsiveStyles = StyleSheet.create({
    fontSize: {
      fontSize: getResponsiveFontSize(4),
    },
  });

  // Using the useMemo hook to cache the map result and let the exerciseTypes variable get re-initialized only of one of the deps changes
  const exerciseTypes = useMemo(() => {
    return TASKS[tasksKey].map((exercise) => {
      return (
        <ExerciseCard
          label={isSelf ? exercise.exerciseSettingsKey : exercise.label}
          iconName={exercise.iconName}
          isActive={activeExerciseSet?.[exercise.exerciseSettingsKey]}
          onPress={() => {
            setActiveExerciseSet((prev) => ({
              ...prev,
              [exercise.exerciseSettingsKey]:
                !prev?.[exercise.exerciseSettingsKey],
            }));
            if (!isSelf) {
              toggleActiveLocalSetSettings(setId, exercise.exerciseSettingsKey);
            }
          }}
        />
      );
    });
  }, [activeExerciseSet]);

  const buttonsRightSide = useMemo(() => {
    const disabled = Object.values(activeExerciseSet).every(
      (value) => value === false,
    );

    return [
      isSelf ? null : (
        <PressableNeolexon
          key="settings"
          style={[styles.darkGrey, responsiveStyles.fontSize]}
          onPress={async () => {
            const exerciseSettings: Set = (await encryptedStorage().getItem(
              setId,
            )) as Set;

            navigate('AufgabenSettings', {
              setType,
              setId,
              exerciseSettings,
            });
          }}
        >
          <FontAwesome
            name="gear"
            size={responsizeIconSizeBig}
            color={colors.darkGrey}
          />
        </PressableNeolexon>
      ),
      <Pressable
        className={`w-full ${disabled ? 'opacity-50' : ''}`}
        key="start"
        onPress={async () => {
          const localWordset = await encryptedStorage().getItem(setId);
          const params = {
            setId,
            patientId,
            setType,
            localWordset,
            isSelf,
            selectedPatient,
            activeSets,
            activatedExerciseModalities: activeExerciseSet,
          };

          const obj = await permission(false, !isPatientApp);

          if (obj?.permissions.plan !== 'gratis') {
            navigate(
              isTextset ? 'AufgabenExerciseText' : 'AufgabenExercise',
              params,
            );
          } else {
            showDialog(PREPARED_DIALOGS.noPermissionDialog());
          }
        }}
        disabled={disabled}
      >
        <View className="items-center">
          <AntDesign
            name="caretright"
            size={responsizeIconSizeBig}
            color={colors.primary}
          />
        </View>
        <Text
          testID="start-exercise"
          className="text-center text-slate-700"
          style={responsiveStyles.fontSize}
        >
          Start
        </Text>
      </Pressable>,
    ];
  }, [
    responsiveStyles,
    responsizeIconSizeBig,
    isSelf ? activeExerciseSet : JSON.stringify(exerciseSettings),
  ]);

  useEffect(() => {
    function beforeRemove() {
      if (isTherapistDemo && toolTipNumber === 4) {
        setToolTipNumber(3);
      }
    }
    navigation.addListener('beforeRemove', beforeRemove);
    return () => {
      navigation.removeListener('beforeRemove', beforeRemove);
    };
  }, []);

  useEffect(() => {
    TASKS[tasksKey].map((exercise) => {
      setActiveExerciseSet((prev) => ({
        ...prev,
        [exercise.exerciseSettingsKey]: isSelf
          ? selfTrainingSelection[exercise.exerciseSettingsKey]
          : exerciseSettings[exercise.exerciseSettingsKey]?.active,
      }));
    });
  }, [selfTrainingSelection, exerciseSettings, tasksKey, isSelf]);

  return (
    <View className="flex-1 bg-blue-neolexonlight">
      <SelectLayout
        leftGrid={<SquareGrid items={exerciseTypes} rows={2} columns={2} />}
        rightGrid={<SquareGrid items={buttonsRightSide} rows={2} columns={1} />}
      />
    </View>
  );
}
